import React, { useState, useEffect, ReactElement, SetStateAction, ChangeEvent } from 'react';

import { RadioOrSelectOption } from '../../../../../../types/common.types';
import { Session, SessionTemplate } from '../../../../../../types/entity.types';

import useToaster, { ToasterActions } from '../../../../../../hooks/use-toaster/useToaster';

import { ARCHIVED, ERROR, timeOptions } from '../../../../../../utils/constants';
import {
  convertDateToTimeString,
  convertTimeStringToDate,
  mapStringOptionsToObjectOptions
} from '../../../../../../utils/util';

import Modal from '../../../../../common/modal/Modal';
import Dropdown from '../../../../../common/input/dropdown/Dropdown';
import DatePicker from '../../../../../common/input/date-picker/DatePicker';
import Button from '../../../../../common/button/Button';
import NumberPicker from '../../../../../common/input/number-picker/NumberPicker';
import Checkbox from '../../../../../common/input/checkbox/Checkbox';
import TextInput from '../../../../../common/input/text-input/TextInput';

import './accountability-attendance-modal.scss';

const REOCCURRENCE_OPTIONS: RadioOrSelectOption[] = [
  { label: 'Once-Off', value: 'once-off' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' }
];

type Props = {
  status: string;
  show: boolean;
  setShow: React.Dispatch<SetStateAction<boolean>>;
  session?: Session;
  updateSession: (session: Partial<Session>) => void;
  generateSessions: (sessionTemplate: SessionTemplate, onSuccess: () => void) => void;
};

const AccountabilityAttendanceModal = ({
  status,
  show,
  setShow,
  session,
  updateSession,
  generateSessions
}: Props): ReactElement => {
  const [templateName, setTemplateName] = useState<string>('');
  const [reoccurrencePattern, setReoccurrencePattern] = useState<RadioOrSelectOption>({
    label: 'Once-Off',
    value: 'once-off'
  });
  const [startTime, setStartTime] = useState<string | undefined>(undefined);
  const [endTime, setEndTime] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dailyFrequency, setDailyFrequency] = useState<string>('1');
  const [everyWeekday, setEveryWeekday] = useState<boolean>(false);
  const [weeklyFrequency, setWeeklyFrequency] = useState<string>('1');
  const [monday, setMonday] = useState<boolean>(false);
  const [tuesday, setTuesday] = useState<boolean>(false);
  const [wednesday, setWednesday] = useState<boolean>(false);
  const [thursday, setThursday] = useState<boolean>(false);
  const [friday, setFriday] = useState<boolean>(false);
  const [saturday, setSaturday] = useState<boolean>(false);
  const [sunday, setSunday] = useState<boolean>(false);
  const [monthlyFrequencyOne, setMonthlyFrequencyOne] = useState<string>('1');
  const [monthlyFrequencyTwo, setMonthlyFrequencyTwo] = useState<string>('1');
  const [specificDayInMonth, setSpecificDayInMonth] = useState<boolean>(true);
  const [dayInMonth, setDayInMonth] = useState<string>('1');
  const [monthlyStringOne, setMonthlyStringOne] = useState<string>('First');
  const [monthlyStringTwo, setMonthlyStringTwo] = useState<string>('Monday');
  const [name, setName] = useState<string>('');
  const [startTimeOptions, setStartTimeOptions] = useState<RadioOrSelectOption[]>(
    mapStringOptionsToObjectOptions(timeOptions)
  );
  const [endTimeOptions, setEndTimeOptions] = useState<RadioOrSelectOption[]>(
    mapStringOptionsToObjectOptions(timeOptions)
  );

  const { showToaster }: ToasterActions = useToaster();

  useEffect((): void => {
    setStartDate(undefined);
    setEndDate(undefined);
  }, [reoccurrencePattern]);

  useEffect((): void => {
    if (session) {
      setName(session.name ? session.name : '');
      setStartTime(convertDateToTimeString(session.startTime));
      setEndTime(convertDateToTimeString(session.endTime));

      if (!session.startDate) return;

      setStartDate(new Date(session.startDate));
    } else {
      setName('');
      setStartTime(undefined);
      setEndTime(undefined);
      setStartDate(undefined);
    }
  }, [session, show]);

  useEffect((): void => {
    const copyOfTimeOptions: string[] = [...timeOptions];

    let filteredTimeOptions: string[] = copyOfTimeOptions;
    if (endTime) {
      filteredTimeOptions = copyOfTimeOptions.filter((timeOption: string): boolean => {
        const timeOptionDate: Date = new Date(convertTimeStringToDate(timeOption));
        const endTimeDate: Date = new Date(convertTimeStringToDate(endTime));

        if (!timeOptionDate || !endTimeDate) return false;

        return timeOptionDate < endTimeDate;
      });
    }

    setStartTimeOptions(mapStringOptionsToObjectOptions(filteredTimeOptions));
  }, [endTime]);

  useEffect((): void => {
    const copyOfTimeOptions: string[] = [...timeOptions];

    let filteredTimeOptions: string[] = copyOfTimeOptions;
    if (startTime) {
      filteredTimeOptions = copyOfTimeOptions.filter((timeOption: string): boolean => {
        const timeOptionDate: Date = new Date(convertTimeStringToDate(timeOption));
        const startTimeDate: Date = new Date(convertTimeStringToDate(startTime));

        if (!timeOptionDate || !startTimeDate) return false;

        return timeOptionDate > startTimeDate;
      });
    }

    setEndTimeOptions(mapStringOptionsToObjectOptions(filteredTimeOptions));
  }, [startTime]);

  const resetFields = (): void => {
    setReoccurrencePattern({ label: 'Once-Off', value: 'once-off' });
    setStartTime(undefined);
    setEndTime(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setDailyFrequency('1');
    setEveryWeekday(false);
    setWeeklyFrequency('1');
    setMonday(false);
    setTuesday(false);
    setWednesday(false);
    setThursday(false);
    setFriday(false);
    setSaturday(false);
    setSunday(false);
    setMonthlyFrequencyOne('1');
    setMonthlyFrequencyTwo('1');
    setSpecificDayInMonth(true);
    setDayInMonth('1');
    setMonthlyStringOne('First');
    setMonthlyStringTwo('Monday');
    setName('');
  };

  const closeModal = (): void => {
    setShow(false);
    resetFields();
  };

  const buildSessionTemplate = (): SessionTemplate | undefined => {
    if (!startTime) {
      showToaster(ERROR, 'Please select a start time');
      return;
    }

    if (!endTime) {
      showToaster(ERROR, 'Please select an end time');
      return;
    }

    let weeklyReoccurrenceDays: string = '';
    if (reoccurrencePattern.value === 'weekly') {
      if (monday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}monday;`;
      }

      if (tuesday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}tuesday;`;
      }

      if (wednesday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}wednesday;`;
      }

      if (thursday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}thursday;`;
      }

      if (friday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}friday;`;
      }

      if (saturday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}saturday;`;
      }

      if (sunday) {
        weeklyReoccurrenceDays = `${weeklyReoccurrenceDays}sunday;`;
      }

      weeklyReoccurrenceDays = weeklyReoccurrenceDays.slice(0, -1);
    }

    const sessionTemplate: SessionTemplate = {
      name: templateName,
      dailyReoccurrence: everyWeekday ? undefined : dailyFrequency,
      everyWeekDay: everyWeekday,
      externalReference: undefined,
      monthlyReoccurrence: specificDayInMonth ? monthlyFrequencyOne : monthlyFrequencyTwo,
      dayOccurrenceInMonth: specificDayInMonth ? dayInMonth : undefined,
      monthlyReoccurrenceDay: specificDayInMonth
        ? undefined
        : `${monthlyStringOne.toLowerCase()};${monthlyStringTwo.toLowerCase()}`,
      weeklyReoccurrence: weeklyFrequency,
      weeklyReoccurrenceDays,
      reoccurrencePattern: reoccurrencePattern.value,
      startTime: convertTimeStringToDate(startTime),
      endTime: convertTimeStringToDate(endTime),
      startDate: startDate,
      endDate: endDate
    };

    return sessionTemplate;
  };

  const handleSaveClick = (): void => {
    if (session) {
      const sessionToUpdate: Partial<Session> = {
        externalReference: session.externalReference,
        startTime: convertTimeStringToDate(startTime),
        endTime: convertTimeStringToDate(endTime),
        startDate: startDate,
        name
      };
      updateSession(sessionToUpdate);
      closeModal();
    } else {
      const sessionTemplate: SessionTemplate | undefined = buildSessionTemplate();

      if (sessionTemplate) {
        generateSessions(sessionTemplate, closeModal);
      }
    }
  };

  const renderDailyOptions = (): ReactElement => (
    <div className="accountability-attendance-modal__daily">
      <div className="accountability-attendance-modal__daily__option">
        <div
          className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--outer"
          onClick={(): void => setEveryWeekday(false)}
          data-testid="daily-radio-option-one-outer"
        >
          {!everyWeekday && (
            <div
              className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--inner"
              data-testid="daily-radio-option-one-inner"
            />
          )}
        </div>
        <p>Every</p>
        <NumberPicker
          value={dailyFrequency}
          onChange={(value: number | string): void => setDailyFrequency(value.toString())}
          hideArrows
        />
        <p>day(s)</p>
      </div>
      <div className="accountability-attendance-modal__daily__option">
        <div
          className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--outer"
          onClick={(): void => setEveryWeekday(true)}
          data-testid="daily-radio-option-two-outer"
        >
          {everyWeekday && (
            <div
              className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--inner"
              data-testid="daily-radio-option-two-inner"
            />
          )}
        </div>
        <p>Every Weekday</p>
      </div>
    </div>
  );

  const renderWeeklyOptions = (): ReactElement => (
    <div className="accountability-attendance-modal__weekly">
      <div className="accountability-attendance-modal__weekly__option">
        <p>Every</p>
        <NumberPicker
          value={weeklyFrequency}
          onChange={(value: string | number): void => setWeeklyFrequency(value.toString())}
          hideArrows
        />
        <p>week(s) on:</p>
      </div>
      <div className="accountability-attendance-modal__weekly__days">
        <Checkbox
          label="Monday"
          name="monday-checkbox"
          value={monday}
          onChange={(value: boolean): void => setMonday(value)}
          left
        />
        <Checkbox
          label="Tuesday"
          name="tuesday-checkbox "
          value={tuesday}
          onChange={(value: boolean): void => setTuesday(value)}
          left
        />
        <Checkbox
          label="Wednesday"
          name="wednesday-checkbox "
          value={wednesday}
          onChange={(value: boolean): void => setWednesday(value)}
          left
        />
        <Checkbox
          label="Thursday"
          name="thursday-checkbox"
          value={thursday}
          onChange={(value: boolean): void => setThursday(value)}
          left
        />
        <Checkbox
          label="Friday"
          name="friday-checkbox"
          value={friday}
          onChange={(value: boolean): void => setFriday(value)}
          left
        />
        <Checkbox
          label="Saturday"
          name="saturday-checkbox"
          value={saturday}
          onChange={(value: boolean): void => setSaturday(value)}
          left
        />
        <Checkbox
          label="Sunday"
          name="sunday-checkbox"
          value={sunday}
          onChange={(value: boolean): void => setSunday(value)}
          left
        />
      </div>
    </div>
  );

  const renderMonthlyOptions = (): ReactElement => (
    <div className="accountability-attendance-modal__monthly">
      <div className="accountability-attendance-modal__monthly__option">
        <div
          className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--outer"
          onClick={(): void => setSpecificDayInMonth(true)}
          data-testid="monthly-radio-option-one-outer"
        >
          {specificDayInMonth && (
            <div
              className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--inner"
              data-testid="monthly-radio-option-one-inner"
            />
          )}
        </div>
        <p>Day</p>
        <NumberPicker
          value={dayInMonth}
          onChange={(value: string | number): void => setDayInMonth(value.toString())}
          hideArrows
        />
        <p>of every</p>
        <NumberPicker
          value={monthlyFrequencyOne}
          onChange={(value: string | number): void => setMonthlyFrequencyOne(value.toString())}
          hideArrows
        />
        <p>month(s)</p>
      </div>
      <div className="accountability-attendance-modal__monthly__option">
        <div
          className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--outer"
          onClick={(): void => setSpecificDayInMonth(false)}
          data-testid="monthly-radio-option-two-outer"
        >
          {!specificDayInMonth && (
            <div
              className="accountability-attendance-modal__radio-button accountability-attendance-modal__radio-button--inner"
              data-testid="monthly-radio-option-two-inner"
            />
          )}
        </div>
        <p>The</p>
        <Dropdown
          value={{ label: monthlyStringOne, value: monthlyStringOne }}
          onChange={(option: RadioOrSelectOption): void => setMonthlyStringOne(option.value)}
          options={mapStringOptionsToObjectOptions(['First', 'Second', 'Third', 'Fourth', 'Last'])}
          inline
        />
        <Dropdown
          value={{ label: monthlyStringTwo, value: monthlyStringTwo }}
          onChange={(option: RadioOrSelectOption): void => setMonthlyStringTwo(option.value)}
          options={mapStringOptionsToObjectOptions([
            'Weekday',
            'Weekend Day',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday'
          ])}
          inline
        />
        <p>of every</p>
        <NumberPicker
          value={monthlyFrequencyTwo}
          onChange={(value: string | number): void => setMonthlyFrequencyTwo(value.toString())}
          hideArrows
        />
        <p>month(s)</p>
      </div>
    </div>
  );

  return (
    <Modal show={show}>
      <div className="accountability-attendance-modal">
        <div className="accountability-attendance-modal__header">{session ? 'Edit Session' : 'Add Sessions'}</div>
        {!session && (
          <div className="accountability-attendance-modal__content">
            <div className="accountability-attendance-modal__session-name">
              <label htmlFor="session-name">Session Name</label>
              <TextInput
                value={templateName}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setTemplateName(target.value)}
                name="session-name"
              />
            </div>
            <div className="accountability-attendance-modal__session-time">
              <p>Session Time</p>
              <div>
                <label htmlFor="start-time">From</label>
                <Dropdown
                  value={startTime ? { label: startTime, value: startTime } : undefined}
                  onChange={(option: RadioOrSelectOption): void => setStartTime(option.value)}
                  options={startTimeOptions}
                  name="start-time"
                  inline
                />
                <label htmlFor="end-time">To</label>
                <Dropdown
                  value={endTime ? { label: endTime, value: endTime } : undefined}
                  onChange={(option: RadioOrSelectOption): void => setEndTime(option.value)}
                  options={endTimeOptions}
                  name="end-time"
                  inline
                />
              </div>
            </div>
            <div className="accountability-attendance-modal__reoccurrence-pattern">
              <label htmlFor="reoccurrence-pattern">Reoccurrence Pattern</label>
              <Dropdown
                value={reoccurrencePattern}
                onChange={(option: RadioOrSelectOption): void => setReoccurrencePattern(option)}
                options={REOCCURRENCE_OPTIONS}
                name="reoccurrence-pattern"
                inline
              />
            </div>

            {reoccurrencePattern.value === 'daily' && renderDailyOptions()}

            {reoccurrencePattern.value === 'weekly' && renderWeeklyOptions()}

            {reoccurrencePattern.value === 'monthly' && renderMonthlyOptions()}

            <div className="accountability-attendance-modal__dates">
              <div>
                <label htmlFor="start-date">{reoccurrencePattern.value === 'once-off' ? 'Date' : 'From Date'}</label>
                <DatePicker value={startDate} onChange={(date: Date): void => setStartDate(date)} name="start-date" />
              </div>
              {reoccurrencePattern.value !== 'once-off' && (
                <div>
                  <label htmlFor="end-date">To Date</label>
                  <DatePicker value={endDate} onChange={(date: Date): void => setEndDate(date)} name="end-date" />
                </div>
              )}
            </div>
          </div>
        )}

        {session && (
          <div className="accountability-attendance-modal__content">
            <div className="accountability-attendance-modal__session-name">
              <label htmlFor="edit-name">Session Name</label>
              <TextInput
                value={name}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setName(target.value)}
                name="edit-name"
                disabled={status === ARCHIVED}
              />
            </div>

            <div className="accountability-attendance-modal__session-time">
              <p>Session Time</p>
              <div>
                <label htmlFor="edit-from-time">From</label>
                <Dropdown
                  value={startTime ? { label: startTime, value: startTime } : undefined}
                  onChange={(option: RadioOrSelectOption): void => setStartTime(option.value)}
                  options={startTimeOptions}
                  name="edit-from-time"
                  disabled={status === ARCHIVED}
                  inline
                />
                <label htmlFor="edit-to-time">To</label>
                <Dropdown
                  value={endTime ? { label: endTime, value: endTime } : undefined}
                  onChange={(option: RadioOrSelectOption): void => setEndTime(option.value)}
                  options={endTimeOptions}
                  name="edit-to-time"
                  disabled={status === ARCHIVED}
                  inline
                />
              </div>
            </div>

            <div className="accountability-attendance-modal__dates">
              <div>
                <label htmlFor="edit-date">Date</label>
                <DatePicker
                  value={startDate}
                  onChange={(date: Date): void => setStartDate(date)}
                  name="edit-date"
                  disabled={status === ARCHIVED}
                />
              </div>
            </div>
          </div>
        )}

        <div className="accountability-attendance-modal__button-container">
          <Button text={status === ARCHIVED ? 'Close' : 'Cancel'} onClick={closeModal} />
          {status !== ARCHIVED && <Button text="Save" onClick={handleSaveClick} />}
        </div>
      </div>
    </Modal>
  );
};

export default AccountabilityAttendanceModal;
